import { useMemo } from "react";
import AudienceUsersList from "@/components/AudienceUsersList";
import { addColumnsToAssessmentReport } from "@/api/recommendedAssignments";
import type { LearnerScore } from "@/api/recommendedAssignments";
import type { DirectoryUserShape, StoredUser, FormValues } from "@/api/assignments";
import type { AvailableUsersMap } from "@/hooks/useAvailableUsers";
import type { MutableRefObject } from "react";

// Explicit enriched shape: LearnerScore fields + directory profile data + normalized name
type MergedLearner = LearnerScore & Pick<DirectoryUserShape, "developerProfile" | "attributes">;
type EnrichedLearner = MergedLearner & { name: { givenName: string; familyName: string } };

// Normalize flat givenName/familyName from KA report → nested name expected by AudienceUsersList columns
const normalizeToNestedName = (u: MergedLearner): EnrichedLearner => ({
  ...u,
  name: { givenName: u.givenName ?? "", familyName: u.familyName ?? "" },
});

// Merge directory user fields (developerProfile, attributes) into a learner score object.
// Assessment fields take priority for overlapping keys (e.g. email, averageScore).
const mergeDirectoryData = (
  score: LearnerScore,
  dirUser: DirectoryUserShape | undefined
): MergedLearner => ({
  developerProfile: dirUser?.developerProfile,
  attributes: dirUser?.attributes,
  ...score,
});

interface RecommendedAudienceProps {
  values: FormValues["target"];
  handleChange: (values: FormValues["target"]) => void;
  onSelectedUsersChange?: (users: DirectoryUserShape[]) => void;
  onAddUsersAndContinue?: MutableRefObject<(() => void) | null>;
  selectedUsersToAdd: DirectoryUserShape[];
  setSelectedUsersToAdd: (users: DirectoryUserShape[]) => void;
  selectedUsersToRemove: DirectoryUserShape[];
  setSelectedUsersToRemove: (users: DirectoryUserShape[]) => void;
  hasPrepopulatedUsers?: boolean;
  directoryUsers?: DirectoryUserShape[];
}

const RecommendedAudience = ({
  values,
  handleChange,
  onSelectedUsersChange,
  onAddUsersAndContinue,
  selectedUsersToAdd,
  setSelectedUsersToAdd,
  selectedUsersToRemove,
  setSelectedUsersToRemove,
  hasPrepopulatedUsers = false,
  directoryUsers = [],
}: RecommendedAudienceProps) => {
  // Build a directoryUuid lookup for O(1) profile merging
  const directoryUserMap = useMemo(() => {
    const map = new Map<string, DirectoryUserShape>();
    directoryUsers.forEach(u => {
      if (u.directoryUuid) map.set(u.directoryUuid, u);
    });
    return map;
  }, [directoryUsers]);

  // Enrich users with assessment score columns (averageScore, proficiency, status),
  // merge directory profile data (developerProfile, attributes), and normalize name shape.
  const enrichedAvailableUsers = useMemo(
    () =>
      addColumnsToAssessmentReport(values.availableUsers ?? [])
        .map(score => mergeDirectoryData(score, directoryUserMap.get(score.directoryUuid ?? "")))
        .map(normalizeToNestedName),
    [values.availableUsers, directoryUserMap]
  );

  const enrichedUsers = useMemo(
    () =>
      addColumnsToAssessmentReport(values.users ?? [])
        .map(score => mergeDirectoryData(score, directoryUserMap.get(score.directoryUuid ?? "")))
        .map(normalizeToNestedName),
    [values.users, directoryUserMap]
  );

  return (
    <AudienceUsersList
      values={{
        ...values,
        users: enrichedUsers as StoredUser[],
        availableUsers: enrichedAvailableUsers,
        allUsers: values.allUsers as Map<string, AvailableUsersMap> | undefined,
      }}
      handleChange={
        handleChange as (v: {
          users: StoredUser[];
          availableUsers: DirectoryUserShape[];
          filteredAvailableUsers?: DirectoryUserShape[];
          allUsers?: Map<string, AvailableUsersMap>;
        }) => void
      }
      showNewHire={false}
      hasPrepopulatedUsers={hasPrepopulatedUsers}
      onSelectedUsersChange={onSelectedUsersChange}
      onAddUsersAndContinue={onAddUsersAndContinue}
      selectedUsersToAdd={selectedUsersToAdd}
      setSelectedUsersToAdd={setSelectedUsersToAdd}
      selectedUsersToRemove={selectedUsersToRemove}
      setSelectedUsersToRemove={setSelectedUsersToRemove}
      entityType="Assignment"
    />
  );
};

export default RecommendedAudience;
